/**
 * File: src/pages/Dashboard/chat/ChatMenu.js
 * Description: Chat sidebar listing conversations with search, filters, pins,
 *              and per-chat actions. Integrates with network and Redux actions.
 * Dependencies: React, Ant Design, custom components, network utils.
 * Guidelines:
 *  - Keep UI strings localizable.
 *  - Document non-trivial helpers and event handlers.
 * Created: 2025-09-23
 * Last Updated: 2025-09-23
 */
 
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Badge, Input, Select } from 'antd';
import AvatarStatus from 'components/shared-components/AvatarStatus';
import { COLOR_1 } from 'constants/ChartConstant';
import { SearchOutlined } from '@ant-design/icons';
import { Spin, Tabs, Dropdown, message } from 'antd';
import globalConstant from 'constants/globalConstant';
import CustomBadge from 'components/CustomBadge';
import './ChatMenu.css';
import { Scrollbars } from 'react-custom-scrollbars';
import { EllipsisOutlined } from '@ant-design/icons';
import network from 'network/network';
import { chatTrace } from 'utils/chatTrace';
import { hasVisibleActivity, isActivityMatch } from './activityUtils';
import { customerStatuses } from 'constants/customerStatus';

const { TabPane } = Tabs;

/**
 * ChatMenu Component
 *
 * Sidebar for chat conversations with search, tab filters, pinning, and actions.
 *
 * @param {Object} props
 * @param {Array} props.chatList - Conversations to display
 * @param {boolean} props.isLoading - Loading state for chat list
 * @param {boolean} props.isLoadingMore - Loading state for pagination
 * @param {boolean} props.hasMore - Whether more items are available
 * @param {Function} props.loadMoreChatListAction - Loads more chat items
 * @param {Function} props.resetUnreadCountactions - Resets unread count for a chat
 * @param {Function} props.onChangeChatIndex - Changes selected chat
 * @param {string|number} props.activeNumber - Active chat/customer number
 * @param {Array} props.filterOptions - Options for filter dropdowns
 * @param {boolean} props.filterOptionsLoading - Loading state for filters
 * @param {Function} props.selectChatListAction - Refreshes chat list
 * @param {Function} props.selectChatFilterOptionsAction - Loads filter options
 * @param {Function} props.changeConversationActivityStatusAction - Changes conversation status
 * @param {Function} props.changeConversationMessageTypeAction - Changes conversation message type
 * @param {Function} props.startManualChatAction - Starts manual chat
 * @param {Function} props.onChatListRefresh - Refreshes chat list
 */
const ChatMenu = ({ 
	chatList, 
	isLoading,
	isLoadingMore,
	hasMore,
	loadMoreChatListAction,
	resetUnreadCountactions,
	onChangeChatIndex, 
	activeNumber,
	filterOptions,
	filterOptionsLoading,
	selectChatListAction,
	selectChatFilterOptionsAction,
	changeConversationActivityStatusAction,
	changeConversationMessageTypeAction,
	startManualChatAction,
	onChatListRefresh
}) => {

	const [searchText, setSearchText] = useState("");
	const [activeTab, setActiveTab] = useState("2");
	const [pinnedChats, setPinnedChats] = useState([]); // Pinned chat numbers

	const [selectedFilter, setSelectedFilter] = useState(null);
	const [customerStatusOverrides, setCustomerStatusOverrides] = useState({});

	const cloneForLog = useCallback((value) => {
		try {
			return JSON.parse(JSON.stringify(value));
		} catch (error) {
			return value;
		}
	}, [onChatListRefresh]);

	const getCustomerStatusOverrideKey = useCallback((customerId, customerNumber) => {
		return customerId ?? customerNumber;
	}, []);

	const resolvecustomerStatus = useCallback((item) => {
		const overrideKey = getCustomerStatusOverrideKey(item?.customerId, item?.customerNumber);
		if (overrideKey !== undefined && overrideKey !== null && customerStatusOverrides[overrideKey] !== undefined) {
			return customerStatusOverrides[overrideKey];
		}

		return item?.customerStatus ?? item?.customerstatus ?? item?.CustomerStatus ?? item?.CUSTOMERSTATUS ?? item?.CUSTOMER_STATUS;
	}, [customerStatusOverrides, getCustomerStatusOverrideKey]);

	const isCustomerBlocked = useCallback((item) => {
		return Number(resolvecustomerStatus(item)) === customerStatuses.passive;
	}, [resolvecustomerStatus]);

	const logActiveTabDatasets = useCallback(({
		rawChatList,
		tabInputCandidates,
		afterAdminFilter,
		afterActivityFilter,
		excludedByAdminFilter,
		excludedByActivityFilter,
		apiBaselineCoverageCount,
		apiBaselineMismatches,
		selectedFilterValue,
		trigger
	}) => {
		const now = new Date().toISOString();
		console.groupCollapsed(`[ChatMenu][AktifMusteriler][${now}] ${trigger}`);
		console.log('meta', {
			activeTab,
			selectedFilter: selectedFilterValue,
			searchText,
			rawChatListCount: rawChatList.length,
			tabInputCount: tabInputCandidates.length,
			afterAdminFilterCount: afterAdminFilter.length,
			afterActivityFilterCount: afterActivityFilter.length,
			excludedByAdminFilterCount: excludedByAdminFilter.length,
			excludedByActivityFilterCount: excludedByActivityFilter.length,
			apiBaselineCoverageCount,
			apiBaselineMismatchCount: apiBaselineMismatches.length
		});

		console.log('rawChatList.full', cloneForLog(rawChatList));
		console.log('tabInputCandidates.full', cloneForLog(tabInputCandidates));
		console.log('afterAdminFilter.full', cloneForLog(afterAdminFilter));
		console.log('afterActivityFilter.full', cloneForLog(afterActivityFilter));
		console.log('excludedByAdminFilter.full', cloneForLog(excludedByAdminFilter));
		console.log('excludedByActivityFilter.full', cloneForLog(excludedByActivityFilter));
		console.log('apiBaselineMismatches.full', cloneForLog(apiBaselineMismatches));

		chatTrace('chatMenu.activeTab.isAdminTransaction.baselineCheck', {
			activeTab,
			selectedFilter: selectedFilterValue,
			apiBaselineCoverageCount,
			apiBaselineMismatchCount: apiBaselineMismatches.length,
			mismatches: apiBaselineMismatches
		}, 'warn');

		console.groupEnd();
	}, [activeTab, cloneForLog, searchText]);

	/**
	 * Formats chat item's time label.
	 * Uses backend provided date/time when available; falls back gracefully.
	 *
	 * @param {Object} item - Chat item containing lastMessageDate/Time or time
	 * @returns {string} Human-readable time or date
	 */
	const formatChatTime = useCallback((item) => {
		try {
			const today = new Date();
			const todayStr = today.toLocaleDateString('tr-TR'); // dd.MM.yyyy formatında bugünün tarihi
			
			// lastMessageDate (tarih) ve lastMessageTime (saat) backend'den geliyor
			if (item.lastMessageDate) {
				let messageDate = item.lastMessageDate;
				let messageTime = item.lastMessageTime; // Backend'den HH:mm formatında
				
				// Backend'den gelen tarih formatını kontrol et
				// ToShortDateString() genelde dd.MM.yyyy veya MM/dd/yyyy formatında gelir
				let parsedMessageDate;
				
				if (typeof messageDate === 'string') {
					// Türkiye locale'i için dd.MM.yyyy formatı beklenir
					if (messageDate.includes('.')) {
						// dd.MM.yyyy formatı
						const [day, month, year] = messageDate.split('.');
						parsedMessageDate = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
					} else if (messageDate.includes('/')) {
						// MM/dd/yyyy veya dd/MM/yyyy formatı
						const parts = messageDate.split('/');
						if (parts.length === 3) {
							// Türk formatına çevir: dd.MM.yyyy
							parsedMessageDate = `${parts[1].padStart(2, '0')}.${parts[0].padStart(2, '0')}.${parts[2]}`;
						}
					} else {
						// Diğer formatlar için direkt parse et
						const date = new Date(messageDate);
						parsedMessageDate = date.toLocaleDateString('tr-TR');
					}
				}
				

				
				if (parsedMessageDate === todayStr) {
					// Bugünkü mesaj - backend'den gelen gerçek saati kullan
					if (messageTime && messageTime.match(/^\d{1,2}:\d{2}$/)) {
						// Backend'den HH:mm formatında saat geldi
						return messageTime;
					} else if (item.time && item.time.match(/^\d{1,2}:\d{2}([AP]M)?$/i)) {
						// Fallback: time alanından saat al
						return item.time;
					} else {
						// Son fallback: şu anki saati göster
						const now = new Date();
						const hours = now.getHours().toString().padStart(2, '0');
						const minutes = now.getMinutes().toString().padStart(2, '0');
						return `${hours}:${minutes}`;
					}
				} else {
					// Geçmiş tarih - tam tarihi göster (dd/MM/yyyy formatında)
					if (parsedMessageDate && parsedMessageDate.includes('.')) {
						// dd.MM.yyyy'yi dd/MM/yyyy'ye çevir
						return parsedMessageDate.replace(/\./g, '/');
					}
					return parsedMessageDate || messageDate;
				}
			}
			
			// Fallback: time alanını kullan
			if (item.time) {
				// Saat formatı (14:30, 4:16PM) - bugünkü mesaj olarak kabul et
				if (item.time.match(/^\d{1,2}:\d{2}([AP]M)?$/i)) {
					return item.time;
				}
				
				// Tam tarih formatı (dd/mm/yyyy) - parse et
				const dateMatch = item.time.match(/^(\d{1,2})[\/.](\d{1,2})[\/.](\d{4})$/);
				if (dateMatch) {
					const [, day, month, year] = dateMatch;
					const formattedDate = `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
					const timeDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
					const timeDateStr = timeDate.toLocaleDateString('tr-TR');
					
					if (timeDateStr === todayStr) {
						// Bugünkü tarih ama saat bilgisi yok - şu anki saati göster
						const now = new Date();
						const hours = now.getHours().toString().padStart(2, '0');
						const minutes = now.getMinutes().toString().padStart(2, '0');
						return `${hours}:${minutes}`;
					} else {
						// Geçmiş tarih
						return formattedDate;
					}
				}
				
				// Tanımlanamayan format - olduğu gibi döndür
				return item.time;
			}
			
			// Hiçbir alan yoksa boş döndür
			return '';
		} catch (error) {
			console.warn('Tarih formatlama hatası:', error, item);
			return item.time || '';
		}
	}, []);

	// Filter options'ı Redux action ile çek
	useEffect(() => {
		if (selectChatFilterOptionsAction) {
			selectChatFilterOptionsAction();
		}
	}, [selectChatFilterOptionsAction]);

	// Default olarak "Hepsi" seçeneğini ayarla (filterOptions yüklendiğinde)
	useEffect(() => {
		if (filterOptions && filterOptions.length > 0 && selectedFilter === null) {
			setSelectedFilter("all"); // "Hepsi" seçeneği varsayılan olarak seçili
		}
	}, [filterOptions, selectedFilter]);

	/**
	 * Builds select options for conversation activity filtering.
	 * Includes a default "Hepsi" option, and only activity IDs 3 and 5.
	 *
	 * @returns {Array<{value:string|number,label:string}>}
	 */
	const getSelectOptions = useCallback(() => {
		const defaultOptions = [{ value: "all", label: "Hepsi" }];
		
		if (filterOptions && filterOptions.length > 0) {
			// Filter to only include activity IDs 3 and 5
			const filteredOptions = filterOptions
				.filter(item => item.activityid === 3 || item.activityid === 5)
				.map(item => ({
					value: item.activityid,
					label: item.activitydesc
				}));
			
			return [
				...defaultOptions,
				...filteredOptions
			];
		}
		
		// Fallback options (API'den veri gelmezse)
		return defaultOptions;
	}, [filterOptions]);

	/**
	 * Opens a chat by id and resets its unread count.
	 *
	 * @param {string|number} id - Conversation identifier
	 */
	const openChat = useCallback(id => {
		// var lastChar = match.url.substr(match.url.length - 1);
		// var split = "/";
		// if (lastChar == "/") {
		// 	split = "";
		// }
		chatTrace('chatMenu.openChat', {
			customerNumber: id
		});

		onChangeChatIndex(id);
		// history.push(`${match.url + split}${id}`)

		resetUnreadCountactions(id);
	}, [onChangeChatIndex, resetUnreadCountactions]);

	/**
	 * Toggles pinning for a conversation by customer number.
	 *
	 * @param {string} customerNumber
	 */
	const handlePinChat = useCallback((customerNumber) => {
		setPinnedChats(prev => prev.includes(customerNumber)
			? prev.filter(num => num !== customerNumber)
			: [...prev, customerNumber]
		);
	}, []);

	/**
	 * Blocks a customer via blockUser API endpoint.
	 *
	 * @param {string} customerId
	 * @param {string} customerNumber
	 * @param {string} customerName
	 */
	const handleBlockChat = useCallback(async (customerId, customerNumber, customerName) => {
		try {
			const params = {
				customerId: customerId
			};

			console.log('[handleBlockChat] calling blockUser', { params });

			const result = await network.get('/admin/Client/blockUser', params);

			console.log('[handleBlockChat] result', result);

			if (result && result.isSuccess) {
				const overrideKey = getCustomerStatusOverrideKey(customerId, customerNumber);
				setCustomerStatusOverrides(prev => ({
					...prev,
					[overrideKey]: customerStatuses.passive
				}));
				message.success(`${customerName} engellendi`);
				console.log('[handleBlockChat] refreshing chat list after block');
				if (selectChatListAction) {
					selectChatListAction({});
				}
				if (onChatListRefresh) {
					onChatListRefresh();
				}
			} else {
				console.warn('[handleBlockChat] unexpected response', result);
				message.error(result?.errorMessage || 'Müşteri engellenemedi');
			}
		} catch (error) {
			console.error('[handleBlockChat] error', error, error?.response?.data || null);
			message.error('Müşteri engellenirken hata oluştu');
		}
	}, [getCustomerStatusOverrideKey, onChatListRefresh, selectChatListAction]);

	/**
	 * Unblocks a customer via unblockUser API endpoint.
	 *
	 * @param {string} customerId
	 * @param {string} customerNumber
	 * @param {string} customerName
	 */
	const handleUnblockChat = useCallback(async (customerId, customerNumber, customerName) => {
		try {
			const params = {
				customerId: customerId
			};

			console.log('[handleUnblockChat] calling unblockUser', { params });
			const result = await network.get('/admin/Client/unblockUser', params);
			console.log('[handleUnblockChat] result', result);

			if (result && result.isSuccess) {
				const overrideKey = getCustomerStatusOverrideKey(customerId, customerNumber);
				setCustomerStatusOverrides(prev => ({
					...prev,
					[overrideKey]: customerStatuses.active
				}));
				message.success(`${customerName} engeli kaldırıldı`);
				console.log('[handleUnblockChat] refreshing chat list after unblock');
				if (selectChatListAction) {
					selectChatListAction({});
				}
				if (onChatListRefresh) {
					onChatListRefresh();
				}
			} else {
				console.warn('[handleUnblockChat] unexpected response', result);
				message.error(result?.errorMessage || 'Engel kaldırılamadı');
			}
		} catch (error) {
			console.error('[handleUnblockChat] error', error, error?.response?.data || null);
			message.error('Engel kaldırılırken hata oluştu');
		}
	}, [getCustomerStatusOverrideKey, onChatListRefresh, selectChatListAction]);

	const handleActivityStatusChange = useCallback(async (customerId,customerNumber, activityId, activityName) => {
		try {
			
			const params = {
				customerNumber: customerNumber,
				activityId: activityId,
				activityName:activityName,
				customerId:customerId
			};
			
			// Skip immediate list fetch to prevent stale backend snapshot from overriding updated activity.
			const result = await changeConversationActivityStatusAction(params, true);
			
			if (result && result.success) {
				message.success(`Aktivite durumu "${activityName}" olarak değiştirildi`);
			} else {
				message.error(result?.error || 'Aktivite durumu değiştirilemedi');
			}
		} catch (error) {
			message.error('Aktivite durumu değiştirilirken hata oluştu');
		}
	}, [changeConversationActivityStatusAction]);

	const handleStartManualChat = useCallback(async (customerId, customerNumber, customerName) => {
		try {
			const params = {
				customerNumber: customerNumber,
				customerId: customerId
			};
			
			const result = await startManualChatAction(params);
			
			if (result && result.success) {
				message.success(`${customerName} ile manuel sohbet başlatıldı`);
				
				// Aktif conversation'ı sıfırla ki kullanıcı yeniden seçtiğinde güncel bilgilerle yüklensin
				if (onChatListRefresh) {
					onChatListRefresh();
				}
			} else {
				message.error(result?.error || 'Manuel sohbet başlatılamadı');
			}
		} catch (error) {
			message.error('Manuel sohbet başlatılırken hata oluştu');
		}
	}, [startManualChatAction, onChatListRefresh]);

	const handleMessageTypeChange = useCallback(async (customerId, customerNumber, messageType) => {
		try {
			const params = {
				customerId: customerId,
				customerNumber: customerNumber,
				messageType: messageType,
				messageTypeName: messageType
			};
			const result = await changeConversationMessageTypeAction(params, true);

			if (result && result.success) {
				message.success(`Sohbet tipi "${messageType}" olarak değiştirildi`);
			} else {
				message.error(result?.error || 'Sohbet tipi değiştirilemedi');
			}
		} catch (error) {
			message.error('Sohbet tipi değiştirilirken hata oluştu');
		}
	}, [changeConversationMessageTypeAction]);

	/**
	 * Builds per-chat dropdown menu including pin/unpin, manual chat, and activity changes.
	 *
	 * @param {Object} item - Chat item
	 * @returns {Array} Menu items config
	 */
	const getChatMenuItems = useCallback((item) => {
		const customerStatus = resolvecustomerStatus(item);
		const isBlocked = isCustomerBlocked(item);
		console.log('[ChatMenu] menu item status snapshot', {
			customerNumber: item?.customerNumber,
			customerName: item?.customerName,
			customerStatus,
			rawcustomerStatus: item?.customerStatus,
			rawcustomerStatus: item?.customerStatus
		});

		const activityMenuItems = [
			{
				key: 'pin',
				label: pinnedChats.includes(item.customerNumber) ? 'Unpinle' : 'Pinle',
				onClick: () => handlePinChat(item.customerNumber)
			}
		];

		// Bloke et / Blokeyi kaldır seçeneği - customerStatus değerine göre belirle
		if (isBlocked) {
			activityMenuItems.push({
				key: 'unblock',
				label: '🔓 Blokeyi Kaldır',
				onClick: () => handleUnblockChat(item.customerId, item.customerNumber, item.customerName)
			});
		} else {
			activityMenuItems.push({
				key: 'block',
				label: '🚫 Bloke Et',
				onClick: () => handleBlockChat(item.customerId, item.customerNumber, item.customerName)
			});
		}

		// Manuel sohbet başlatma seçeneği - admin transaction aktif değilse ve son transaction 24 saatten eskiyse göster
		if (!item.isAdminTransaction && !item.isLastTransactionOlderThan24Hours) {
			activityMenuItems.push({
				key: 'start-manual-chat',
				label: '📞 Manuel Sohbete Başla',
				onClick: () => handleStartManualChat(item.customerId, item.customerNumber, item.customerName)
			});
		}

		let hasMenuDivider = false;

		// Add activity options only for open transactions
		if (filterOptions && filterOptions.length > 0 && item.isOpenTransaction) {
			activityMenuItems.push({ type: 'divider' });
			hasMenuDivider = true;
			
			// Add submenu for activity status change
			const activitySubMenuItems = filterOptions.map(activity => ({
				key: `activity-${activity.activityid}`,
				label: `${activity.activitydesc}${isActivityMatch(item.activityId, activity.activityid) ? ' ✓' : ''}`,
				onClick: () => handleActivityStatusChange(item.customerId, item.customerNumber, activity.activityid, activity.activitydesc),
				disabled: isActivityMatch(item.activityId, activity.activityid)
			}));

			activityMenuItems.push({
				key: 'activity-status',
				label: 'Aktivite Durumu Değiştir',
				children: activitySubMenuItems
			});
		}

		const messageTypeSubMenuItems = [
			{ value: 'Satış İçerikli', label: 'Satış İçerikli' },
			{ value: 'Satış Dışı', label: 'Satış Dışı' },
			{ value: 'Niteliksiz', label: 'Niteliksiz' }
		].map(messageType => ({
			key: `msg-type-${messageType.value}`,
			label: `${messageType.label}${item.messageType === messageType.value ? ' ✓' : ''}`,
			onClick: () => handleMessageTypeChange(item.customerId, item.customerNumber, messageType.value),
			disabled: item.messageType === messageType.value
		}));

		if (!hasMenuDivider) {
			activityMenuItems.push({ type: 'divider' });
		}

		activityMenuItems.push({
			key: 'message-type',
			label: 'Sohbet Tipi Değiştir',
			children: messageTypeSubMenuItems
		});

		return activityMenuItems;
	}, [pinnedChats, filterOptions, handlePinChat, handleBlockChat, handleUnblockChat, handleStartManualChat, handleActivityStatusChange, handleMessageTypeChange, resolvecustomerStatus, isCustomerBlocked, onChatListRefresh]);

	/**
	 * Handles search input changes.
	 *
	 * @param {Event} e - Input change event
	 */
	const searchOnChange = e => {
		const query = e.target.value;
		setSearchText(query);
	}

	//const id = parseInt(location.pathname.match(/\/([^/]+)\/?$/)[1])

	// Memoize filtered and sorted chat list to ensure updates propagate
	// Must be called before any early returns (React Hooks rule)
	const newchatList = useMemo(() => {
		let filteredList = Array.isArray(chatList) ? JSON.parse(JSON.stringify(chatList)) : [];
		
		if (searchText.length > 0) {
			filteredList = filteredList.filter(function (el) {
				return (el.customerNumber.toUpperCase().indexOf(searchText.toUpperCase()) !== -1 || (el.customerName || "").toUpperCase().indexOf(searchText.toUpperCase()) !== -1)
			});
		}

		// Pinlenmiş chat'leri listenin başına al
		filteredList.sort((a, b) => {
			const aPinned = pinnedChats.includes(a.customerNumber);
			const bPinned = pinnedChats.includes(b.customerNumber);
			if (aPinned === bPinned) return 0;
			return aPinned ? -1 : 1;
		});

		return filteredList;
	}, [chatList, searchText, pinnedChats]);

	useEffect(() => {
		const totalChats = newchatList.length;
		const adminTransactionCount = newchatList.filter(item => !!item.isAdminTransaction).length;
		const openTransactionCount = newchatList.filter(item => !!item.isOpenTransaction).length;
		const openButNotAdmin = newchatList.filter(item => !!item.isOpenTransaction && !item.isAdminTransaction);

		chatTrace('chatMenu.snapshot', {
			activeTab,
			selectedFilter,
			totalChats,
			adminTransactionCount,
			openTransactionCount,
			openButNotAdminCount: openButNotAdmin.length
		});

		if (openButNotAdmin.length > 0) {
			chatTrace('chatMenu.activeTab.exclusionCandidates', {
				activeTab,
				excludedCount: openButNotAdmin.length,
				sample: openButNotAdmin.slice(0, 5).map(item => ({
					customerNumber: item.customerNumber,
					customerName: item.customerName,
					isOpenTransaction: item.isOpenTransaction,
					isAdminTransaction: item.isAdminTransaction,
					activityId: item.activityId,
					activityName: item.activityName
				}))
			}, 'warn');
		}
	}, [newchatList, activeTab, selectedFilter]);

	useEffect(() => {
		const activeTabCandidates = newchatList.filter(item => !!item.isAdminTransaction);
		const filteredActiveCandidates = selectedFilter === 'all'
			? activeTabCandidates
				: activeTabCandidates.filter(item => isActivityMatch(item.activityId, selectedFilter));

		chatTrace('chatMenu.activeTab.metrics', {
			activeTab,
			selectedFilter,
			beforeActivityFilter: activeTabCandidates.length,
			afterActivityFilter: filteredActiveCandidates.length
		});
	}, [newchatList, selectedFilter, activeTab]);

	useEffect(() => {
		if (activeTab !== "2") {
			return;
		}

		const rawChatList = Array.isArray(chatList) ? chatList : [];
		const tabInputCandidates = Array.isArray(newchatList) ? newchatList : [];
		const afterAdminFilter = tabInputCandidates.filter(item => !!item.isAdminTransaction);

		const shouldFilterByActivity = selectedFilter !== "all";
		const afterActivityFilter = shouldFilterByActivity
			? afterAdminFilter.filter(item => isActivityMatch(item.activityId, selectedFilter))
			: afterAdminFilter;

		const excludedByAdminFilter = tabInputCandidates.filter(item => !item.isAdminTransaction);
		const excludedByActivityFilter = shouldFilterByActivity
			? afterAdminFilter.filter(item => !isActivityMatch(item.activityId, selectedFilter))
			: [];

		const apiBaselineMismatches = tabInputCandidates
			.filter(item => item.__apiIsAdminTransaction !== undefined && item.isAdminTransaction !== item.__apiIsAdminTransaction)
			.map(item => ({
				customerNumber: item.customerNumber,
				customerName: item.customerName,
				apiIsAdminTransaction: item.__apiIsAdminTransaction,
				currentIsAdminTransaction: item.isAdminTransaction,
				isOpenTransaction: item.isOpenTransaction,
				isOpenAdmin: item.isOpenAdmin,
				activityId: item.activityId,
				apiActivityId: item.__apiActivityId,
				apiAuditRequestId: item.__apiAuditRequestId,
				apiAuditPage: item.__apiAuditPage,
				apiAuditIndex: item.__apiAuditIndex
			}));

		const apiBaselineCoverageCount = tabInputCandidates
			.filter(item => item.__apiIsAdminTransaction !== undefined)
			.length;

		logActiveTabDatasets({
			rawChatList,
			tabInputCandidates,
			afterAdminFilter,
			afterActivityFilter,
			excludedByAdminFilter,
			excludedByActivityFilter,
			apiBaselineCoverageCount,
			apiBaselineMismatches,
			selectedFilterValue: selectedFilter,
			trigger: 'active_tab_dataset_snapshot'
		});
	}, [activeTab, chatList, newchatList, selectedFilter, logActiveTabDatasets]);

	/**
	 * Renders a badge for admin transactions.
	 *
	 * @param {Object} item - Chat item
	 * @returns {JSX.Element|undefined}
	 */
	const getAdminTransactionBadge = (item) => {

		if (item.isAdminTransaction) {

			return (
				<CustomBadge>

				</CustomBadge>
			);
		}
	}

	/**
	 * Handles scroll event for infinite scroll pagination.
	 * Triggers loadMore when user scrolls near the bottom.
	 *
	 * @param {Object} values - Scroll values from Scrollbars
	 */
	const handleScroll = useCallback((values) => {
		const { scrollTop, scrollHeight, clientHeight } = values;
		// Scroll pozisyonu sonun %80'ine ulaştığında yeni veri yükle
		const scrollThreshold = 0.8;
		const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
		
		if (scrollPercentage >= scrollThreshold && hasMore && !isLoadingMore && loadMoreChatListAction) {
			loadMoreChatListAction();
		}
	}, [hasMore, isLoadingMore, loadMoreChatListAction]);

	/**
	 * Renders message list for a given tab key.
	 * Applies admin/open filters and activity filter.
	 *
	 * @param {"1"|"2"} tabKey
	 * @returns {JSX.Element}
	 */
	const getTabMessageList = useCallback((tabKey) => {
		var tabData = JSON.parse(JSON.stringify(newchatList));

		if (tabKey === "2") {
			tabData = tabData.filter(function (el) {
				return (el.isAdminTransaction);
			});

			// Select box filtrelemesi (sadece "Müşteri Temsilcisine bağlananlar" tabında)
			if (selectedFilter !== "all") {
				tabData = tabData.filter(function (el) {
					// Activity ID'ye göre filtreleme

					
					return isActivityMatch(el.activityId, selectedFilter);
				});
			}
		}

		return (
			<div id="chat-menu-list6">
				{/* Aktivite filtresi - sadece Aktif Müşteriler tabında göster */}
				{tabKey === "2" && (
					<div className="chat-menu-activity-filter">
						<Select
							style={{ width: '100%' }}
							placeholder="Aktivite filtresi seçin"
							value={selectedFilter}
							onChange={setSelectedFilter}
							options={getSelectOptions()}
							loading={filterOptionsLoading}
						/>
					</div>
				)}
				<Scrollbars 
					style={{ height: tabKey === "2" ? 'calc(100vh - 390px)' : 'calc(100vh - 350px)' }}
					onScrollFrame={handleScroll}
				>
					<div style={{ padding: '0 10px' }}>
						{tabData.map((item, i) => (
						<div
							style={{
								position: "relative"
							}}
							key={`chat-item-${item.customerNumber}`}
							onClick={() => openChat(item.customerNumber)}
							className={`chat-menu-list-item ${i === (newchatList.length - 1) ? 'last' : ''} ${item.customerNumber == activeNumber ? 'selected' : ''} ${isCustomerBlocked(item) ? 'blocked' : ''}`}
						>
							{/* İşlem Durumu Belirteci - Hide if chat is ended (isOpenTransaction === false) */}
							{item.isOpenTransaction !== false && (
								<div
									style={{
										position: 'absolute',
										top: 8,
										left: 8,
										width: 12,
										height: 12,
										borderRadius: '50%',
										backgroundColor: item.isAdminTransaction ? '#52c41a' : (item.isOpenTransaction ? '#ff9603' : '#d9d9d9'),
										border: '2px solid #fff',
										boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
										zIndex: 1
									}}
									title={item.isAdminTransaction ? 'Admin Aktif' : (item.isOpenTransaction ? 'Aktif İşlem' : 'Pasif İşlem')}
								/>
							)}
							<AvatarStatus 
								src={globalConstant.imageTypes.getImageUrl(globalConstant.imageTypes.customerImage.type, item.customerImage)}
								name={`${pinnedChats.includes(item.customerNumber) ? '📌 ' : ''}${item.customerName}`} 
								suffix={isCustomerBlocked(item) ? (
									<span className="chat-menu-blocked-label" title="Blokeli kullanıcı">
										Blokeli
									</span>
								) : null}
								subTitle={
									<div>
										{hasVisibleActivity(item.activityId, item.activityName) && (
											<div style={{ fontSize: '11px', color: '#1890ff', fontWeight: 'bold', marginBottom: '2px' }}>
												🏷️ {item.activityName}
											</div>
										)}
										<div style={{ fontSize: '12px', color: '#666' }}>
											{item.lastWrittenMessage}
										</div>
									</div>
								} 
							/>
							<div className="text-right">
								<div className="chat-menu-list-item-time">{formatChatTime(item)}</div>
								{item.unreadMessageCount === 0 ? <span></span> : <Badge count={item.unreadMessageCount} style={{ backgroundColor: COLOR_1 }} />}
							</div>
							{/* Üç nokta menüsü */}
							<Dropdown menu={{ items: getChatMenuItems(item) }} trigger={['click']} placement="bottomRight">
								<button
									onClick={e => e.stopPropagation()}
									title="Daha fazla"
									style={{
										position: 'absolute',
										top: 8,
										right: 8,
										background: 'none',
										border: 'none',
										cursor: 'pointer',
										fontSize: 18,
										color: '#aaa',
									}}
								>
									<EllipsisOutlined />
								</button>
							</Dropdown>
						</div>
					))}
					{/* Loading indicator for pagination */}
					{isLoadingMore && (
						<div style={{ textAlign: 'center', padding: '15px 0' }}>
							<Spin size="small" tip="Daha fazla yükleniyor..." />
						</div>
					)}
					{/* End of list message */}
					{!hasMore && tabData.length > 0 && (
						<div style={{ textAlign: 'center', padding: '15px 0', color: '#999', fontSize: '12px' }}>
							Tüm mesajlar yüklendi
						</div>
					)}
				</div>
			</Scrollbars>
			</div>
		);
	}, [newchatList, selectedFilter, activeNumber, filterOptions, filterOptionsLoading, pinnedChats, getSelectOptions, openChat, formatChatTime, getChatMenuItems, handleScroll, isLoadingMore, hasMore, isCustomerBlocked]);

	// Memoize tab items to ensure they update when chatList or filters change
	// Must be called before any early returns (React Hooks rule)
	const tabItems = useMemo(() => [
		{
			key: "1",
			label: "Tüm Müşteriler",
			children: getTabMessageList("1"),
		},
		{
			key: "2",
			label: "Aktif Müşteriler",
			children: getTabMessageList("2"),
		},
	], [getTabMessageList]);

	if (isLoading) {
		return (
			<Spin tip="Mesajlar Yükleniyor..." style={{ width: "100%", height: "100%", marginTop: 25 }}>
			</Spin>
		);
	}

	/**
	 * Renders chat tabs container with proper items and state.
	 *
	 * @returns {JSX.Element}
	 */
	const getChatTab = () => {
		return (
			<div style={{ marginLeft: "10px", marginRight: "10px", height: '100%' }}>
				
				<Tabs 
					defaultActiveKey={activeTab} 
					onChange={setActiveTab} 
					items={tabItems}
					centered
				>
				</Tabs>
				{/* Select box tabların hemen altında göster */}
				
			</div>
		);
	}

	return (
		<div className="chat-menu">
			<div className="chat-menu-toolbar">
				<Input
					className="chat-menu-search-input"
					placeholder="Kullanıcılarda Ara"
					onChange={searchOnChange}
					prefix={
						<SearchOutlined className="font-size-md mr-2" />
					}
				/>

			</div>
			<div className="chat-menu-list">
				{getChatTab()}
			</div>
		</div>
	)
}

export default ChatMenu
